import type {ReactNode, ComponentProps} from 'react';
import React from 'react';

import {Button} from '@pb/kit-component-button';

import {Dialog} from '../dialog/dialog';
import {DialogHeader} from '../dialog-header/dialog-header';
import {DialogFooter} from '../dialog-footer/dialog-footer';

import type {ButtonProps} from '@pb/kit-component-button';
import type {OverlayProps} from '../overlay/overlay';
import type {DialogHeaderProps} from '../dialog-header/dialog-header';
import type {DialogFooterProps} from '../dialog-footer/dialog-footer';

type WrapperProps = ComponentProps<'div'>;

const DEFAULT_WRAPPER_PROPS: WrapperProps = {};

export type AlertDialogProps = {
    /**
     * Dialog content
     */
    children: ReactNode,
    /**
     * Props for the (Confirm) button
     */
    confirmButtonProps?: ButtonProps,
    /**
     * Props for the (Cancel) button
     */
    cancelButtonProps?: ButtonProps,
    /**
     * Text for confirm button
     * @default 'Confirm'
     */
    confirmText?: ReactNode,
    /**
     * Text for cancel button
     * @default 'Cancel'
     */
    cancelText?: ReactNode,
    /**
     * Dialog title - displayed in header
     */
    title: ReactNode,
    /**
     * Props for DialogHeader component
     */
    headerProps?: DialogHeaderProps,
    /**
     * Props for DialogFooter component
     */
    footerProps?: DialogFooterProps,
    /**
     * Dialog content wrapper native DIV props
     */
    wrapperProps?: WrapperProps,
} & OverlayProps;


/**
 * Pre-configured confirmation dialog with `role="alertdialog"` for destructive or
 * irreversible actions.
 *
 * Renders a `Dialog` with a `DialogHeader` (title), the caller-supplied body
 * (`children`), and a `DialogFooter` containing primary Confirm and secondary
 * Cancel buttons. Button text defaults to `"Confirm"` / `"Cancel"` and can be
 * overridden via `confirmText` / `cancelText`. Pass `confirmButtonProps` or
 * `cancelButtonProps` to add handlers, disable buttons, or change their appearance.
 *
 * ## Accessibility
 * `role="alertdialog"` causes screen readers to announce the dialog immediately
 * on open. Supply a descriptive `title` — it maps to `aria-labelledby` via
 * `DialogHeader`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/overlays-dialog--documentation}
 */
export function AlertDialog({
    children,
    confirmButtonProps,
    cancelButtonProps,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    title,
    headerProps,
    footerProps,
    wrapperProps = DEFAULT_WRAPPER_PROPS,
    ...dialogProps
}: AlertDialogProps) {
    return (
        <Dialog
            {...dialogProps}
            {...wrapperProps}
            roleProps={{
                role: 'alertdialog',
            }}>
            <DialogHeader {...headerProps}>
                {title}
            </DialogHeader>

            {children}

            <DialogFooter {...footerProps}>
                <Button
                    variant="Type.PRIMARY"
                    {...confirmButtonProps}>
                    {confirmText}
                </Button>
                <Button
                    variant="Type.SECONDARY"
                    {...cancelButtonProps}>
                    {cancelText}
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
